import React from "react";
import {
  Badge,
  Box,
  Card,
  CardBody,
  CardHeader,
  Divider,
  Heading,
  HStack,
  List,
  ListItem,
  SimpleGrid,
  Stack,
  Text,
} from "@chakra-ui/react";
import {
  FiCheckCircle,
  FiCreditCard,
  FiFileText,
  FiHelpCircle,
  FiRepeat,
  FiSearch,
  FiTrendingUp,
} from "react-icons/fi";

const PaymentsHelpPanel = () => {
  return (
    <Stack spacing={6}>
      <Card variant="outline">
        <CardHeader>
          <HStack spacing={3}>
            <FiHelpCircle />
            <Box>
              <Heading size="sm">Payments Help</Heading>
              <Text color="gray.600" fontSize="sm">
                A quick guide to every payments feature in this dashboard.
              </Text>
            </Box>
          </HStack>
        </CardHeader>
        <CardBody>
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
            <Box>
              <Heading size="xs" textTransform="uppercase" color="gray.500" mb={2}>
                Overview
              </Heading>
              <List spacing={2} fontSize="sm" color="gray.700">
                <ListItem>
                  <Badge colorScheme="green" mr={2}>
                    KPI
                  </Badge>
                  Invoice Revenue totals successful invoice payments.
                </ListItem>
                <ListItem>
                  <Badge colorScheme="orange" mr={2}>
                    KPI
                  </Badge>
                  Refunded Amount totals refunded payments.
                </ListItem>
                <ListItem>
                  <Badge colorScheme="blue" mr={2}>
                    KPI
                  </Badge>
                  MRR (v1) sums active plan amounts monthly.
                </ListItem>
                <ListItem>
                  Activity Feed merges invoice and subscription events by date.
                </ListItem>
                <ListItem>
                  Alerts highlight refunds, cancels, and failures.
                </ListItem>
              </List>
            </Box>
            <Box>
              <Heading size="xs" textTransform="uppercase" color="gray.500" mb={2}>
                Search + Filters
              </Heading>
              <List spacing={2} fontSize="sm" color="gray.700">
                <ListItem>
                  <FiSearch style={{ display: "inline", marginRight: 6 }} />
                  Search matches invoice number, customer email, Stripe IDs, and transaction IDs.
                </ListItem>
                <ListItem>
                  Date range filters activity, subscriptions, and invoices in Overview.
                </ListItem>
                <ListItem>
                  Refresh reloads invoices, payments, subscriptions, and subscription invoices.
                </ListItem>
              </List>
            </Box>
          </SimpleGrid>
        </CardBody>
      </Card>

      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
        <Card variant="outline">
          <CardHeader>
            <HStack spacing={3}>
              <FiFileText />
              <Heading size="sm">Invoices</Heading>
            </HStack>
          </CardHeader>
          <CardBody>
            <List spacing={2} fontSize="sm" color="gray.700">
              <ListItem>Use Create Invoice to issue a new invoice.</ListItem>
              <ListItem>All Invoices shows every invoice with its status.</ListItem>
              <ListItem>Payments shows invoices that are paid successfully.</ListItem>
              <ListItem>Refunds shows invoices refunded through payments history.</ListItem>
            </List>
          </CardBody>
        </Card>

        <Card variant="outline">
          <CardHeader>
            <HStack spacing={3}>
              <FiCreditCard />
              <Heading size="sm">Payments</Heading>
            </HStack>
          </CardHeader>
          <CardBody>
            <List spacing={2} fontSize="sm" color="gray.700">
              <ListItem>Payments are tied to invoices through payment history.</ListItem>
              <ListItem>Refunds appear in the Invoices Refunds view.</ListItem>
              <ListItem>
                Status badges: SUCCESS/PAID/ACTIVE (green), REFUNDED/CANCELED (orange), FAILED (red).
              </ListItem>
            </List>
          </CardBody>
        </Card>

        <Card variant="outline">
          <CardHeader>
            <HStack spacing={3}>
              <FiRepeat />
              <Heading size="sm">Subscriptions</Heading>
            </HStack>
          </CardHeader>
          <CardBody>
            <List spacing={2} fontSize="sm" color="gray.700">
              <ListItem>Create Plan to define billing amounts and cadence.</ListItem>
              <ListItem>Create Subscription to enroll a customer on a plan.</ListItem>
              <ListItem>All Subscriptions lists active and canceled records.</ListItem>
              <ListItem>Subscription Customers shows deduped customers with status filter.</ListItem>
              <ListItem>Payments shows subscription invoices from Stripe.</ListItem>
              <ListItem>Refunds shows subscription invoices with refunded status.</ListItem>
            </List>
          </CardBody>
        </Card>

        <Card variant="outline">
          <CardHeader>
            <HStack spacing={3}>
              <FiTrendingUp />
              <Heading size="sm">Reports (Future)</Heading>
            </HStack>
          </CardHeader>
          <CardBody>
            <List spacing={2} fontSize="sm" color="gray.700">
              <ListItem>Revenue combines invoice and subscription revenue.</ListItem>
              <ListItem>MRR / Churn will show recurring revenue trends.</ListItem>
              <ListItem>Disputes and Taxes placeholders are ready for later wiring.</ListItem>
            </List>
          </CardBody>
        </Card>
      </SimpleGrid>

      <Card variant="outline">
        <CardHeader>
          <HStack spacing={3}>
            <FiCheckCircle />
            <Heading size="sm">Tips for Daily Use</Heading>
          </HStack>
        </CardHeader>
        <CardBody>
          <List spacing={2} fontSize="sm" color="gray.700">
            <ListItem>Use the Create menu for quick access to invoice and subscription actions.</ListItem>
            <ListItem>Open Overview first to see KPIs, alerts, and recent activity.</ListItem>
            <ListItem>Use customer email search to quickly find invoices or subscriptions.</ListItem>
            <ListItem>For refunds or cancellations, confirm in the dialog before applying changes.</ListItem>
          </List>
        </CardBody>
      </Card>

      <Divider />
      <Text fontSize="xs" color="gray.500">
        Need more help? Contact support or check webhook status under Utilities.
      </Text>
    </Stack>
  );
};

export default PaymentsHelpPanel;
